/*
  Foxkit quantity-breaks shim (CS-53).

  Two live defects, both inside the Foxkit app's own CDN asset `quantity-breaks.js` (extension
  version `foxkit-upsell-bundles-469`), both reproduced on every run and both root-caused in
  `Website/docs/incidents/foxkit-multibuy-variant-2026-08-11.md`. Neither is reachable from theme
  code and neither depends on anything the theme does.

  DEFECT 1, the wrong colour, silently. The app's `ON_VARIANT_CHANGE` handler fans out to every
  tier, and each tier's `onChange()` force-writes the single product-level variant into EVERY
  per-item dropdown:

      onChange(){ this.variant = this.container?.selectedVariant,
        this.variant && (this.variantSelects.length &&
          this.variantSelects.forEach(t => { t.value = this.variant.id }),
          this.checkQuantityRule(), this.updatePrices()) }

  The shopper's second colour is overwritten with no message and nothing to undo. Worse, that
  method never calls `updateSelectedItems()`, and assigning `.value` from script fires no `change`,
  so the tier's own listener does not run either: `selectedItems` keeps whatever it last computed
  and `handleSubmit()` posts it directly. Measured: the page read Dark Pink on the swatch and on
  both dropdowns while the request posted `{"items":[{"id":"39398070681683","quantity":2}]}`, which
  is Baby Pink. That is a wrong-goods-shipped path, silent to the shopper and to us.

  DEFECT 2, the discount silently does not apply. `updateQuantity()`, which pushes the tier's
  `min_qty` into the theme's product form, is called from the container's radio `change` handler
  and, at init, only when `layout === 'card'`. All four offers on this store are `rich`, and every
  offer renders its first tier already checked (`default_selected: 0`), so a shopper who accepts
  the preselected tier never fires a `change`: the form keeps quantity 1 and "Get this deal!" adds
  ONE unit at full price. This is the branch the Ganesha, Wedding Cards and Seed Paper offers take,
  134 products, and Wedding Cards preselects the 50 unit tier.

  DEFECT 3, the tier prices go stale the moment the shopper changes variant. Added 16 August after
  the founder reported that wedding cards and seed paper "just pick up the price of the first
  variant". Reproduced on both. The cause is one missing call in the same `onChange()` above: it
  refreshes `this.variant` and then calls `updatePrices()`, but `updatePrices()` does not price from
  `this.variant`. It prices from `this.selectedItems`:

      updatePrices(){ ... this.totalPrice = this.selectedItems.reduce(
        (t,e) => t + this.variants.find(t=>t.id==e.id).price * e.quantity, 0) ... }

  and `onChange()` never calls `updateSelectedItems()`, so `selectedItems` keeps the variant the
  page loaded with, for the whole session. Measured on a wedding card: switching the envelope from
  None to Basil moved the variant from Rs 99.00 to Rs 189.00, and the "Buy 50" tier kept reading
  Rs 4,950.00 when the truth is Rs 9,450.00. Measured on Basil Seed Paper: 24 x 34 inches to A5
  moved Rs 90.00 to Rs 6.30, and the "Buy 100" tier kept reading Rs 9,000.00 against a true
  Rs 630.00, which OVERSTATES by 14.3x. Both directions are reachable.

  This is a DISPLAY defect only, and that distinction was checked rather than assumed. On these
  offers `show_variant_selector` is false, so `handleSubmit()` takes its else branch and clicks the
  theme's own Add to Cart button, and the theme's form carries the correct variant. The cart and the
  price charged are right; the number the shopper reads before clicking is wrong.

  Why the existing DEFECT 1 repair did not already cover it: `repairTiers()` opened with
  `if (!selects.length ...) return`, so it only ever ran on offers that render per-item dropdowns.
  Of the four offers only Rakhi does. The three that do not, Ganesha, Wedding Cards and Seed Paper,
  fell straight through. Affected in practice: about 100 of the 103 wedding cards and 12 of the 24
  seed papers (the other 12 are DRAFT). All 7 Ganesha products are single-variant, so they cannot
  reach this defect at all.

  WHAT THIS DOES. It subscribes to Foxkit's own event bus (`window.FoxkitEvents`), which is the
  pattern `assets/foxkit-cart-bridge.js` (CS-21) has run on since the 8 August release, and repairs
  the state the app leaves behind. It patches no app code, forks no app file and duplicates no
  theme handler. Every entry point feature-detects and stands down silently, so a page with no
  offer, a page where the app never loads, or a future extension version with a different API shape
  gets a no-op rather than an exception.

  RE-VERIFY ON EVERY FOXKIT EXTENSION UPDATE. `updateSelectedItems`, `updatePrices` and
  `updateQuantity` are the app's own methods: stable in 469, but undocumented and unowned by us.
  The harness is `Website/scripts/foxkit-multibuy-probe-cdp.js`.

  CS-78 (17 Aug 2026), the founder's no-default-tier ruling, and it RETIRES part of CS-53.

  Target behaviour, in the founder's words: Foxkit defaults to NONE of its tiers selected, and the
  quantity box carries the product's MOQ, on every product family.

  THE MOQ SOURCE, measured on the live PDP (this was the design doc's open blocker): it is NOT the
  lowest Foxkit tier. Wedding-card tiers start at 50 while the founder's MOQ is 25. The MOQ lives in
  the AVADA ORDER LIMIT app (`window.AVADA_ORDER_LIMIT`), whose per-collection rules carry the
  numbers and the messages ("The Minimum Order Quantity (MOQ) for Wedding Cards is ...", "... for
  Seed Paper is ... sheets"), and which ALREADY writes that minimum into the theme's quantity box
  after it initialises. The page opened at 50 because CS-53's primePreselectedTier() kept
  re-asserting the preselected tier's quantity over Avada's 25 for the whole poll window. Our own
  repair was the last writer.

  So the fix is subtraction, not addition:
  1. DESELECT the app's preselected tier once per container, before the shopper touches anything.
     With nothing checked there is no phantom tier price and nothing for a repair to prime.
  2. RETIRE primePreselectedTier() and both of its guards. It existed solely because the
     preselected tier never fired a `change` event. With no preselection, every tier selection IS a
     real `change`, and the app's own handler does the full job (updateQuantity included) on every
     layout. The quantity box now has exactly one non-shopper writer left: Avada, the MOQ owner.
  3. HIDE the app's "Get this deal!" button until a tier is checked, in CSS (global.css, CS-78):
     `foxkit-quantity-breaks:not(:has(input:checked)) .f-quantity-breaks__button-add`. The app's
     `hide_submit_button` config cannot do this on the `rich` layout, measured and recorded in
     `Website/docs/foxkit-moq-default-design-2026-08-17.md`.
  4. NO below-MOQ min attribute on the input. Avada owns enforcement and its cart-level messages;
     a second enforcer would fight it. This resolves the design doc's open founder call.

  DEFECT 1 and DEFECT 3 repairs (repairTiers) are UNTOUCHED: wrong-goods and stale-price repair on
  variant change is orthogonal to what is selected by default.

  CS-83 (18 Aug 2026), the founder's QA items W1 and W7. Scope: ONLY the offers CS-82 already
  marks `data-cs-single-cta` (no per-item variant selectors: wedding, seed paper, ganesha).
  Rakhi is untouched on every path; its app button genuinely submits a multi-colour basket and
  W10 owns its unification.

  W1, two-way tier/quantity sync. The app already syncs tier to quantity (a real tier click runs
  updateQuantity). The missing direction is quantity to tier: type 100 and the Buy-100 tier
  should light up; type back down to the MOQ and no tier should stay lit. On every quantity
  input we check the HIGHEST tier whose min_qty <= value, or none when the value sits below the
  lowest tier. TWO deliberate non-actions are the loop guard: this sync never writes the
  quantity input, and it never dispatches synthetic events. A programmatic radio.checked write
  fires no change, so the app's handler does not run updateQuantity, so the shopper's own number
  is never stomped (typing 300 keeps 300 with Buy-250 lit; a synthetic change would snap it to
  250). The tier discount itself is applied server-side from the CART QUANTITY, proven by the
  cart-level SPECIAL OFFER read on 18 Aug, so a lit tier is honest whether clicked or typed to.

  W7, the deal morph, the founder's evolution of CS-82. While a tier is active the page's one
  Add to Cart IS the deal button: the app's own button_label and the theme's solid-button
  colours, via data-deal-active plus the CS-83 CSS in global.css. It reverts when no tier is
  checked, and a disabled (sold out) button never morphs.
*/
(() => {
  const POLL_MS = 300;
  const GIVE_UP_MS = 20000; // The app bundle loads after the theme assets, then fetches its config.

  // What the shopper actually chose in each per-item dropdown, keyed by the dropdown itself. A
  // dropdown the shopper has never touched is deliberately absent, so it stays free to follow the
  // product's own colour swatch, which is what that control is for. Only a real choice is defended.
  const chosen = new WeakMap();

  document.addEventListener(
    'change',
    (event) => {
      const select = event.target;
      if (!select || select.tagName !== 'SELECT' || typeof select.closest !== 'function') return;
      if (!select.closest('foxkit-quantity-breaks-choice')) return;
      chosen.set(select, select.value);
    },
    true
  );

  // Defect 1. Restore what the shopper chose, then recompute the tier's basket from what is now on
  // screen, so the request can no longer disagree with the page.
  function repairTiers() {
    document.querySelectorAll('foxkit-quantity-breaks-choice').forEach((choice) => {
      if (typeof choice.updateSelectedItems !== 'function') return;
      const selects = Array.from(choice.querySelectorAll('select'));

      if (selects.length) {
        selects.forEach((select) => {
          const wanted = chosen.get(select);
          if (wanted === undefined || select.value === wanted) return;
          // The variant the shopper picked may not be offered any more. Leaving the app's own value
          // in place is correct; forcing a value the <select> does not carry would blank it.
          if (!Array.from(select.options).some((option) => option.value === wanted)) return;
          select.value = wanted;
        });

        // A blank dropdown cannot produce a line item, and the app's own updatePrices() throws on an
        // id it cannot find in productJson. Leave the widget exactly as it is rather than half-repair.
        if (selects.some((select) => !select.value)) return;
      } else if (!choice.variant) {
        // DEFECT 3, the no-selector branch. With no per-item dropdowns the app's own
        // updateSelectedItems() reads `this.variant.id`, so without a variant it would throw.
        // Standing down leaves the stale price, which is the defect, not a new one.
        return;
      }

      choice.updateSelectedItems();
      if (typeof choice.updatePrices === 'function') choice.updatePrices();
    });
  }

  // CS-78. Deselect the app's preselected tier, once per container, before the shopper can have
  // interacted with it. `default_selected: 0` renders the first tier already checked; unchecking it
  // returns the widget to "nothing chosen", which hides the app's submit button (the CS-78 CSS in
  // global.css) and leaves the quantity box to its one rightful non-shopper writer, the Avada
  // Order Limit app, which seeds the product's MOQ. A radio the SHOPPER checks later fires a real
  // `change` and the app's own handler runs its full flow, updateQuantity included, so the retired
  // primePreselectedTier() repair is not needed on any path any more.
  const deselected = new WeakSet();
  function deselectDefaultTier() {
    document.querySelectorAll('foxkit-quantity-breaks').forEach((container) => {
      // No domNodes means the app has not finished rendering this element; try again next tick.
      if (!container.domNodes || deselected.has(container)) return;
      const inputs = container.querySelectorAll(
        'foxkit-quantity-breaks-choice input[type="radio"]'
      );
      if (!inputs.length) return;
      inputs.forEach((input) => {
        input.checked = false;
      });
      // CS-82, founder ruling: on offers WITHOUT per-item variant selectors the app's own submit
      // button only ever clicked the theme's Add to Cart, so two buttons did one job. Mark those
      // containers and the CS-82 CSS hides the app button for good; the tier click still writes
      // its quantity and the page's single Add to Cart does the adding. Rakhi (the one offer
      // WITH selectors) keeps the app button, which genuinely submits its multi-colour basket.
      if (container.config && !container.config.show_variant_selector) {
        container.setAttribute('data-cs-single-cta', '');
      }
      deselected.add(container);
    });
  }

  // CS-83, W7. The morph is judged from the live DOM every time: tier checked and button able.
  function syncDealCta(container, scope) {
    const btn = scope.querySelector('button.add-to-cart-btn');
    if (!btn) return;
    const active = !btn.disabled && !!container.querySelector('input[type="radio"]:checked');
    if (active && !btn.querySelector('.cs-deal-label')) {
      const label = document.createElement('span');
      label.className = 'cs-deal-label';
      label.textContent = (container.config && container.config.button_label) || 'Get this deal!';
      btn.appendChild(label);
    }
    btn.toggleAttribute('data-deal-active', active);
  }

  // CS-83, W1. One wiring per single-CTA container, poll-driven like everything else here.
  const tierSyncWired = new WeakSet();
  function wireTierQtySync() {
    document.querySelectorAll('foxkit-quantity-breaks[data-cs-single-cta]').forEach((container) => {
      if (tierSyncWired.has(container)) return;
      const tiers = container.config && container.config.discounts;
      if (!Array.isArray(tiers) || !tiers.length) return;
      const scope = container.closest('[data-product-section]') || document;
      // The theme renders the quantity input OUTSIDE the form, linked by the form attribute.
      const qty = scope.querySelector('input[name="quantity"]');
      if (!qty) return;
      if (!container.querySelector('foxkit-quantity-breaks-choice input[type="radio"]')) return;

      const syncFromQty = () => {
        const value = parseInt(qty.value, 10);
        // The highest tier whose min_qty <= value ("falls in the bracket"); none below the lowest.
        let target = -1;
        if (!Number.isNaN(value)) {
          tiers.forEach((tier, index) => {
            if (tier.min_qty <= value) target = index;
          });
        }
        container
          .querySelectorAll('foxkit-quantity-breaks-choice input[type="radio"]')
          .forEach((radio) => {
            radio.checked = Number(radio.value) === target;
          });
        syncDealCta(container, scope);
      };

      qty.addEventListener('input', syncFromQty);
      qty.addEventListener('change', syncFromQty);
      // A real tier click: the app itself writes that tier's min_qty into the box (a script
      // write, so no input event and no re-entry here); only the morph needs us.
      container.addEventListener('change', (event) => {
        if (event.target && event.target.type === 'radio') syncDealCta(container, scope);
      });
      // A variant switch can sell out (disabled button) or restock; re-judge the morph.
      document.body.addEventListener('cascade:product:variantchange', () =>
        syncDealCta(container, scope)
      );
      tierSyncWired.add(container);
    });
  }

  function guard(fn) {
    try {
      fn();
    } catch (error) {
      // ponytail: standing down leaves the shopper exactly the widget they have on the live site
      // today. A shim that throws on a page it does not understand is worse than the defect.
    }
  }

  let subscribed = false;
  function subscribe() {
    if (!window.FoxkitEvents || typeof window.FoxkitEvents.subscribe !== 'function') return false;
    // The app's handler runs synchronously inside the emit, so one frame later it has finished
    // whichever order the two subscriptions were made in. Nothing here depends on winning a race.
    window.FoxkitEvents.subscribe('ON_VARIANT_CHANGE', () =>
      requestAnimationFrame(() => guard(repairTiers))
    );
    return true;
  }

  // ponytail: a flat bounded poll for the app's async init, the same shape and budget
  // foxkit-cart-bridge.js uses on the same bus. Both jobs are idempotent, so a tick that finds
  // nothing costs one querySelectorAll, which is all a product page with no offer ever pays.
  const poll = setInterval(() => {
    if (!subscribed) subscribed = subscribe();
    guard(deselectDefaultTier);
    // After the deselect: the CS-82 marker it sets is what scopes the CS-83 wiring.
    guard(wireTierQtySync);
  }, POLL_MS);
  setTimeout(() => clearInterval(poll), GIVE_UP_MS);
})();
